import { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';

// Section shell for the Settings page: anchor id for the side-nav, icon, title, description, optional header action.
export function SettingsSection({ id, icon: Icon, title, description, action, children, testid }) {
  return (
    <section id={id} className="scroll-mt-24 group" data-testid={testid || `settings-section-${id}`}>
      <div className="rounded-sm border border-[#E4E4E7] bg-white transition-shadow duration-200 hover:shadow-sm">
        <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#E4E4E7]">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-sm bg-[#0A0A0A] text-white shrink-0">
              <Icon size={16} weight="bold" />
            </span>
            <div>
              <h2 className="text-base font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
              {description && <p className="text-xs text-[#52525B] mt-0.5">{description}</p>}
            </div>
          </div>
          {action}
        </header>
        <div className="px-6 py-5">{children}</div>
      </div>
    </section>
  );
}

export function FieldLabel({ children, hint, className = '' }) {
  return (
    <Label className={`text-[10px] uppercase tracking-[0.2em] font-semibold text-[#52525B] ${className}`}>
      {children}
      {hint && <span className="ml-1.5 text-[#A1A1AA] normal-case tracking-normal font-normal">— {hint}</span>}
    </Label>
  );
}

export function Hint({ children, className = '' }) {
  return <p className={`text-[11px] leading-relaxed text-[#A1A1AA] mt-1.5 ${className}`}>{children}</p>;
}

// Sticky left navigation; highlights the section currently in view.
export function SettingsNav({ sections }) {
  const [active, setActive] = useState(sections[0]?.id);
  const observer = useRef(null);
  const clickLock = useRef(0);

  useEffect(() => {
    observer.current?.disconnect();
    observer.current = new IntersectionObserver(
      (entries) => {
        if (Date.now() < clickLock.current) return;
        const sc = document.querySelector('main') || document.documentElement;
        const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 8;
        if (atBottom) { setActive(sections[sections.length - 1].id); return; }
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    sections.forEach(s => { const el = document.getElementById(s.id); if (el) observer.current.observe(el); });
    return () => observer.current?.disconnect();
  }, [sections]);

  const go = (id) => {
    clickLock.current = Date.now() + 900;
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav className="lg:sticky lg:top-20 space-y-0.5" data-testid="settings-nav" aria-label="Settings sections">
      {sections.map(s => {
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => go(s.id)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm border-l-2 transition-colors duration-200
              ${isActive ? 'border-[#0A0A0A] bg-[#FAFAFA] text-[#0A0A0A] font-semibold' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A] hover:bg-[#FAFAFA]'}`}
            data-testid={`settings-nav-${s.id}`}
          >
            <s.icon size={15} weight={isActive ? 'bold' : 'regular'} className="shrink-0" />
            <span className="truncate">{s.title}</span>
            {s.badge}
          </button>
        );
      })}
    </nav>
  );
}
